import { BotId } from '@feedyou/schema'
import { prompt } from '../bootbox'
import { deriveBotIdFromName, deriveProdBotIdFromDevBotId } from '../bot'

type Data = {
  existingBotIds: BotId[]
  deletedBotIds: BotId[]
  allReservedIds: BotId[]
}

type Config = {
  allowExisting: boolean
  allowCurrent: boolean
  allowReserved: boolean
}

export const promptForBotNameAndId = async (
  data: Data,
  config: Config,
  title: string,
  value?: string
): Promise<{ id: BotId; name: string } | null> => {
  const { existingBotIds, deletedBotIds, allReservedIds } = data
  const { allowExisting = false, allowReserved = false } = config

  const name = await prompt({ title, value })

  if (name === null) {
    return null
  }

  const retry = (newTitle: string) => promptForBotNameAndId(data, config, newTitle || title, name)

  if (!name) {
    return retry('Bot name cannot be empty')
  }

  const proposedResult = { name, id: deriveBotIdFromName(name) }

  if (existingBotIds.includes(proposedResult.id)) {
    return allowExisting
      ? proposedResult
      : retry('Bot with given name already exists, please choose a different name')
  }

  if (deletedBotIds.includes(proposedResult.id)) {
    return allowExisting
      ? proposedResult
      : retry(
          'This name is reserved, because a bot with this name has existed and been deleted in the past. Please choose a different name.'
        )
  }

  if (allReservedIds.includes(proposedResult.id)) {
    const prodId = deriveProdBotIdFromDevBotId(proposedResult.id)

    return allowReserved
      ? proposedResult
      : retry(
          `Bot with given name is reserved for DEV environment of an existing or historic bot "${prodId}", please choose a different name`
        )
  }

  return proposedResult
}
